import { useState, useEffect } from 'react'
import { 
  Container,
  Box, 
  VStack, 
  Heading, 
  SimpleGrid, 
  Stat, 
  StatLabel, 
  StatNumber, 
  Spinner, 
  useToast, 
  HStack,
  Card,
  CardBody,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Text,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  StatHelpText,
  Divider,
  Badge,
  Textarea
} from '@chakra-ui/react'
import { ChevronRightIcon, CopyIcon } from '@chakra-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import { FiRefreshCw } from 'react-icons/fi'
import apiClient from '../../../../utils/apiClient'

const SystemInfo = () => {
  const [tenant, setTenant] = useState(null)
  const [totalSites, setTotalSites] = useState(0)
  const [activeUsers, setActiveUsers] = useState(0)
  const [totalContent, setTotalContent] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const toast = useToast()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]

  const fetchData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true)
    
    try {
      // Fetch data from multiple endpoints in parallel
      const [tenantData, sitesData, usersData] = await Promise.all([
        apiClient(`/tenants/${tenantId}`),
        apiClient(`/sites/${tenantId}`),
        apiClient(`/users/${tenantId}`)
      ])

      // Try to fetch content data (optional)
      let contentData = []
      try {
        const contentResponse = await apiClient(`/content-entries/${tenantId}`)
        contentData = contentResponse.data || []
      } catch (error) {
        console.log('Content data not available')
      }

      // Update state with API data
      setTenant(tenantData)
      setTotalSites(sitesData?.length || 0)
      setActiveUsers(usersData?.length || 0)
      setTotalContent(contentData?.length || 0)
    } catch (error) {
      toast({
        title: 'Error fetching system information',
        description: error.message,
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!tenantId) return
    fetchData()
  }, [tenantId])

  const handleRefresh = () => {
    fetchData(true)
  }

  const copyToClipboard = (value, label) => {
    navigator.clipboard.writeText(JSON.stringify(value))
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
      status: "success",
      duration: 2000,
    })
  }

  const renderFieldInput = (key, value) => {
    const isObject = typeof value === 'object' && value !== null
    const displayValue = isObject ? JSON.stringify(value, null, 2) : String(value)
    
    if (isObject) {
      return (
        <Textarea
          value={displayValue}
          isReadOnly
          bg="gray.50"
          fontFamily="monospace"
          fontSize="sm"
          minH="80px"
          resize="vertical"
        />
      )
    }
    
    return (
      <Input
        value={displayValue}
        isReadOnly
        bg="gray.50"
      />
    )
  }

  if (loading) {
    return (
      <Container maxW="container.2xl" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading system information...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings">Settings</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>System Information</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Heading size="lg" mb={2}>System Information</Heading>
            <Text color="gray.500">
              View your organization details and system statistics
            </Text>
          </Box>
          <Tooltip label="Refresh Data">
            <IconButton
              icon={<FiRefreshCw />}
              onClick={handleRefresh}
              isLoading={refreshing}
              variant="outline"
              aria-label="Refresh system information"
            />
          </Tooltip>
        </HStack>

        {/* System Status Alert */}
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="medium">System Status: Operational</Text>
            <Text fontSize="sm">
              All systems are running normally. Last updated: {new Date().toLocaleString()}
            </Text>
          </VStack>
        </Alert>
        
        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Organization</StatLabel>
                <StatNumber fontSize="lg" color="blue.500">
                  {tenant?.name || 'Unknown'}
                </StatNumber>
                <StatHelpText>Organization name</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Sites</StatLabel>
                <StatNumber color="green.500">{totalSites}</StatNumber>
                <StatHelpText>Active websites</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Active Users</StatLabel>
                <StatNumber color="purple.500">{activeUsers}</StatNumber>
                <StatHelpText>System users</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Content Entries</StatLabel>
                <StatNumber color="orange.500">{totalContent}</StatNumber>
                <StatHelpText>Total content items</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Organization Details */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <VStack spacing={6} align="stretch">
              <HStack justify="space-between" align="center">
                <Heading size="md">Organization Details</Heading>
                <Badge colorScheme="green" variant="subtle">
                  Active Account
                </Badge>
              </HStack>
              
              <Divider />

              <VStack spacing={4} align="stretch">
                {tenant && Object.entries(tenant).map(([key, value]) => (
                  <FormControl key={key}>
                    <FormLabel textTransform="capitalize" fontWeight="medium">
                      {key.replace(/_/g, ' ')}
                    </FormLabel>
                    <InputGroup>
                      {renderFieldInput(key, value)}
                      <InputRightElement>
                        <Tooltip label={`Copy ${key}`}>
                          <IconButton
                            size="sm"
                            icon={<CopyIcon />}
                            onClick={() => copyToClipboard(value, key)}
                            variant="ghost"
                            aria-label={`Copy ${key}`}
                          />
                        </Tooltip>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                ))}
              </VStack>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  This information is read-only. Contact your administrator to make changes to organization settings.
                </Text>
              </Alert>
            </VStack>
          </CardBody>
        </Card>

        {/* Additional System Information */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="sm">Environment Information</Heading>
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">Environment</Text>
                    <Badge colorScheme="blue" variant="subtle">Production</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">Version</Text>
                    <Text fontSize="sm">v2.1.0</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">Last Backup</Text>
                    <Text fontSize="sm">Today, 3:00 AM</Text>
                  </HStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="sm">Support Information</Heading>
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">Support Plan</Text>
                    <Badge colorScheme="green" variant="subtle">Premium</Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">Response Time</Text>
                    <Text fontSize="sm">&lt; 4 hours</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">Account Manager</Text>
                    <Text fontSize="sm">Available</Text>
                  </HStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>
    </Container>
  )
}

export default SystemInfo
